import React from 'react';
import { Link } from 'react-router-dom';
import {
  BookOpen,
  Sparkles,
  User,
  CheckCircle2,
  ShieldCheck,
  Globe,
  ArrowRight,
  ExternalLink
} from 'lucide-react';
import { useLanguage } from '../LanguageContext';
import { t } from '../translations';

// Verklig fackgranskad artikel med verifierad och fungerande DOI till Nature
export const REAL_SAMPLE_ARTICLE = {
  venue: 'Nature',
  url: 'https://doi.org/10.1038/s41586-023-06063-y',
  authors: 'L. A. Rojas, Z. Sethna, K. C. Soares et al. · Memorial Sloan Kettering',
  content: {
    Swedish: {
      topic: 'Immunologi & Onkologi',
      title: 'Individanpassat mRNA-vaccin framkallar hållbart immunsvar mot bukspottkörtelcancer',
      date: '10 maj 2023',
      authors: 'L. A. Rojas, Z. Sethna, K. C. Soares et al. · Memorial Sloan Kettering',
      summaryHeading: '🎯 Sammanfattning',
      summaryText: 'Forskare har i en klinisk fas I-studie utvärderat ett skräddarsytt mRNA-vaccin mot bukspottkörtelcancer. Genom att sekvensera varje patients specifika tumör skapades individanpassade vacciner riktade mot upp till 20 unika mutationer (neoantigener). Behandlingen kombinerades med immunterapi och standardkirurgi för att träna immunförsvarets T-celler att eliminera mikroskopiska tumörrester.',
      contextHeading: '🌏 Större sammanhang',
      contextText: "Bukspottkörtelcancer är en av de dödligaste tumörformerna och har historiskt betraktats som immunologiskt 'kall' – det vill säga okänslig för modern immunterapi. Denna studie visar att individanpassade mRNA-vacciner kan väcka ett målinriktat immunsvar även vid tumörer med låg mutationsbörda, vilket öppnar en helt ny väg för adjuvant precisionsonkologi.",
      resultsHeading: '🔑 Nyckelresultat',
      results: [
        { bold: 'Hög svarsfrekvens och långvarigt immunsvar', text: '8 av 16 patienter (50 %) utvecklade mätbara och kraftiga T-cellssvar specifika för tumörens neoantigener, vilka kvarstod upp till två år efter behandlingen.' },
        { bold: 'Signifikant fördröjt återfall', text: 'Patienter med verifierat T-cellsvar uppvisade signifikant längre återfallsfri överlevnad jämfört med icke-svarare under studiens uppföljningsperiod.' }
      ]
    },
    English: {
      topic: 'Immunology & Oncology',
      title: 'Personalized RNA neoantigen vaccines stimulate T cells in pancreatic cancer',
      date: '10 May 2023',
      authors: 'L. A. Rojas, Z. Sethna, K. C. Soares et al. · Memorial Sloan Kettering',
      summaryHeading: '🎯 Summary',
      summaryText: 'In a phase I clinical trial, researchers evaluated a personalized mRNA vaccine for pancreatic cancer. By sequencing individual patient tumors, tailored vaccines targeting up to 20 unique neoantigens were synthesized. The regimen was administered alongside immunotherapy and surgery to prime host T cells against residual microscopic disease.',
      contextHeading: '🌏 Broader context',
      contextText: "Pancreatic ductal adenocarcinoma is typically immunologically 'cold' and unresponsive to conventional immune checkpoint blockade. This study provides proof-of-concept that personalized neoantigen mRNA vaccines can provoke clinically durable immunity even in low-mutation tumors, establishing a new therapeutic paradigm in adjuvant oncology.",
      resultsHeading: '🔑 Key results',
      results: [
        { bold: 'Robust and durable immunogenicity', text: '8 out of 16 patients (50%) mounted neoantigen-specific T-cell responses that persisted for up to two years post-vaccination.' },
        { bold: 'Prolonged recurrence-free survival', text: 'Patients demonstrating vaccine-induced T-cell activity experienced significantly longer recurrence-free survival compared to non-responders.' }
      ]
    },
    Norwegian: {
      topic: 'Immunologi & Onkologi',
      title: 'Persontilpasset mRNA-vaksine utløser varig immunrespons mot bukspyttkjertelkreft',
      date: '10. mai 2023',
      authors: 'L. A. Rojas, Z. Sethna, K. C. Soares et al. · Memorial Sloan Kettering',
      summaryHeading: '🎯 Sammendrag',
      summaryText: 'Forskere har i en klinisk fase I-studie evaluert en skreddersydd mRNA-vaksine mot bukspyttkjertelkreft. Ved å sekvensere hver pasients spesifikke svulst ble det utviklet persontilpassede vaksiner rettet mot opptil 20 unike mutasjoner (neoantigener). Behandlingen ble kombinert med immunterapi og kirurgi for å trene immunforsvarets T-celler til å bekjempe mikroskopiske svulstrester.',
      contextHeading: '🌏 Større sammenheng',
      contextText: 'Bukspyttkjertelkreft er en av de mest dødelige kreftformene og har historisk blitt ansett som immunologisk «kald» – altså lite mottakelig for moderne immunterapi. Studien viser at persontilpassede mRNA-vaksiner kan utløse en målrettet immunrespons selv i svulster med lav mutasjonsbyrde, noe som åpner en ny vei innen adjuvant presisjonsonkologi.',
      resultsHeading: '🔑 Nøkkelresultater',
      results: [
        { bold: 'Høy responsrate og langvarig immunitet', text: '8 av 16 pasienter (50 %) utviklet målbare og kraftige T-celleresponser spesifikke for svulstens neoantigener, som vedvarte i opptil to år etter behandlingen.' },
        { bold: 'Betydelig forlenget tilbakefallsfri overlevelse', text: 'Pasienter med bekreftet T-cellerespons hadde signifikant lengre tilbakefallsfri overlevelse sammenlignet med pasienter uten immunrespons i oppfølgingsperioden.' }
      ]
    },
    Danish: {
      topic: 'Immunologi & Onkologi',
      title: 'Personlig mRNA-vaccine fremkalder varigt immunrespons mod bugspytkirtelkræft',
      date: '10. maj 2023',
      authors: 'L. A. Rojas, Z. Sethna, K. C. Soares et al. · Memorial Sloan Kettering',
      summaryHeading: '🎯 Resumé',
      summaryText: 'Forskere har i et klinisk fase I-studie evalueret en skræddersyet mRNA-vaccine mod bugspytkirtelkræft. Ved at sekventere hver patients specifikke tumor blev der fremstillet individuelt tilpassede vacciner målrettet op til 20 unikke mutationer (neoantigener). Behandlingen blev kombineret med immunterapi og kirurgi for at træne immunsystemets T-celler til at eliminere mikroskopiske tumorrester.',
      contextHeading: '🌏 Større sammenhæng',
      contextText: 'Bugspytkirtelkræft er en af de mest dødelige kræftformer og er historisk blevet betragtet som immunologisk »kold« – det vil sige ufølsom over for moderne immunterapi. Dette studie viser, at personlige mRNA-vacciner kan fremkalde et målrettet immunrespons selv ved tumorer med lav mutationsbyrde, hvilket åbner et helt nyt spor inden for adjuverende præcisionsonkologi.',
      resultsHeading: '🔑 Nøgleresultater',
      results: [
        { bold: 'Høj responsrate og langvarigt immunrespons', text: '8 ud af 16 patienter (50 %) udviklede målbare og kraftige T-celleresponser specifikke for tumorens neoantigener, som varede i op til to år efter vaccinationen.' },
        { bold: 'Signifikant forlænget tilbagefaldsfri overlevelse', text: 'Patienter med verificeret T-cellerespons udviste signifikant længere tilbagefaldsfri overlevelse sammenlignet med patienter uden respons i opfølgningsperioden.' }
      ]
    },
    Finnish: {
      topic: 'Immunologia & Onkologia',
      title: 'Yksilöllinen mRNA-rokote käynnistää pitkäkestoisen immuunivasteen haimasyöpää vastaan',
      date: '10. toukokuuta 2023',
      authors: 'L. A. Rojas, Z. Sethna, K. C. Soares et al. · Memorial Sloan Kettering',
      summaryHeading: '🎯 Tiivistelmä',
      summaryText: 'Tutkijat arvioivat vaiheen I kliinisessä tutkimuksessa räätälöityä mRNA-rokotetta haimasyöpään. Sekvensoimalla kunkin potilaan kasvain kehitettiin yksilöllisiä rokotteita, jotka kohdistuivat enintään 20 ainutlaatuiseen mutaatioon (neoantigeeniin). Hoito yhdistettiin immunoterapiaan ja leikkaukseen, jotta T-solut oppisivat tuhoamaan mikroskooppiset kasvainjäämät.',
      contextHeading: '🌏 Laajempi konteksti',
      contextText: 'Haimasyöpä on yksi tappavimmista syöpämuodoista, ja sitä on perinteisesti pidetty immunologisesti ”kylmänä” eli heikosti immunoterapiaan reagoivana. Tutkimus osoittaa, että yksilölliset mRNA-rokotteet voivat herättää kohdennetun immuunivasteen myös vähämutaatioisissa kasvaimissa, mikä avaa uuden suunnan täsmäonkologialle.',
      resultsHeading: '🔑 Keskeiset tulokset',
      results: [
        { bold: 'Vahva ja pitkäkestoinen immuunivaste', text: '8 potilaalla 16:sta (50 %) kehittyi mitattava ja voimakas neoantigeenispesifinen T-soluvaste, joka säilyi jopa kaksi vuotta rokotuksen jälkeen.' },
        { bold: 'Merkittävästi pidempi uusiutumaton elinaika', text: 'Potilailla, joilla todettiin rokotteen käynnistämä T-soluvaste, syövän uusiutumaton elinaika oli seuranta-aikana merkittävästi pidempi kuin muilla potilailla.' }
      ]
    },
    Italian: {
      topic: 'Immunologia e Oncologia',
      title: 'I vaccini personalizzati a mRNA stimolano una risposta duratura delle cellule T nel tumore del pancreas',
      date: '10 maggio 2023',
      authors: 'L. A. Rojas, Z. Sethna, K. C. Soares et al. · Memorial Sloan Kettering',
      summaryHeading: '🎯 Sintesi',
      summaryText: 'In uno studio clinico di fase I, i ricercatori hanno valutato un vaccino a mRNA personalizzato contro il tumore del pancreas. Sequenziando il tumore specifico di ciascun paziente, sono stati sintetizzati vaccini su misura diretti contro fino a 20 mutazioni uniche (neoantigeni), somministrati insieme a immunoterapia e chirurgia per addestrare le cellule T a eliminare i residui tumorali microscopici.',
      contextHeading: '🌏 Contesto più ampio',
      contextText: "L'adenocarcinoma duttale pancreatico è storicamente considerato immunologicamente 'freddo' e resistente all'immunoterapia convenzionale. Questo studio dimostra che i vaccini a mRNA personalizzati possono innescare un'immunità mirata e duratura anche in tumori a basso carico mutazionale, aprendo una nuova strada per l'oncologia di precisione adiuvante.",
      resultsHeading: '🔑 Risultati chiave',
      results: [
        { bold: 'Elevata immunogenicità e risposta duratura', text: '8 pazienti su 16 (50%) hanno sviluppato forti risposte delle cellule T specifiche per i neoantigeni tumorali, persistenti fino a due anni dopo il trattamento.' },
        { bold: 'Sopravvivenza libera da recidiva prolungata', text: 'I pazienti con risposta verificata delle cellule T hanno mostrato una sopravvivenza libera da recidiva significativamente più lunga rispetto ai non-responder.' }
      ]
    },
    French: {
      topic: 'Immunologie & Oncologie',
      title: 'Des vaccins à ARNm personnalisés stimulent durablement les lymphocytes T dans le cancer du pancréas',
      date: '10 mai 2023',
      authors: 'L. A. Rojas, Z. Sethna, K. C. Soares et al. · Memorial Sloan Kettering',
      summaryHeading: '🎯 Résumé',
      summaryText: 'Dans un essai clinique de phase I, des chercheurs ont évalué un vaccin à ARNm personnalisé contre le cancer du pancréas. En séquençant la tumeur propre à chaque patient, des vaccins sur mesure ciblant jusqu’à 20 néoantigènes uniques ont été conçus. Le traitement a été combiné à l’immunothérapie et à la chirurgie pour entraîner les lymphocytes T à éliminer la maladie résiduelle microscopique.',
      contextHeading: '🌏 Contexte général',
      contextText: 'L’adénocarcinome pancréatique est historiquement considéré comme immunologiquement « froid » et réfractaire aux immunothérapies classiques. Cette étude démontre que des vaccins à ARNm personnalisés peuvent déclencher une immunité ciblée et durable même dans des tumeurs à faible charge mutationnelle, ouvrant une nouvelle voie en oncologie de précision adjuvante.',
      resultsHeading: '🔑 Résultats clés',
      results: [
        { bold: 'Immunogénicité robuste et durable', text: '8 patients sur 16 (50 %) ont développé des réponses fortes de lymphocytes T spécifiques aux néoantigènes, persistant jusqu’à deux ans après la vaccination.' },
        { bold: 'Survie sans récidive significativement prolongée', text: 'Les patients présentant une réponse lymphocytaire T induite par le vaccin ont bénéficié d’une survie sans récidive nettement plus longue que les non-répondeurs.' }
      ]
    },
    German: {
      topic: 'Immunologie & Onkologie',
      title: 'Personalisierte mRNA-Neoantigen-Impfstoffe aktivieren T-Zellen bei Bauchspeicheldrüsenkrebs',
      date: '10. Mai 2023',
      authors: 'L. A. Rojas, Z. Sethna, K. C. Soares et al. · Memorial Sloan Kettering',
      summaryHeading: '🎯 Zusammenfassung',
      summaryText: 'In einer klinischen Phase-I-Studie untersuchten Forschende einen maßgeschneiderten mRNA-Impfstoff gegen Bauchspeicheldrüsenkrebs. Durch die Sequenzierung individueller Patiententumoren wurden personalisierte Impfstoffe gegen bis zu 20 spezifische Mutationen (Neoantigene) hergestellt und mit Immuntherapie sowie Chirurgie kombiniert, um T-Zellen gezielt gegen mikroskopische Tumorreste zu richten.',
      contextHeading: '🌏 Größerer Zusammenhang',
      contextText: 'Das duktale Pankreaskarzinom gilt als immunologisch „kalt“ und spricht kaum auf herkömmliche Immuntherapien an. Diese Studie liefert den klinischen Nachweis, dass personalisierte mRNA-Impfstoffe selbst bei Tumoren mit geringer Mutationslast eine wirksame und anhaltende Immunantwort auslösen können – ein Meilenstein für die adjuvante Präzisionsonkologie.',
      resultsHeading: '🔑 Wichtigste Ergebnisse',
      results: [
        { bold: 'Starke und langanhaltende T-Zell-Antwort', text: '8 von 16 Patienten (50 %) entwickelten ausgeprägte neoantigenspezifische T-Zell-Antworten, die bis zu zwei Jahre nach der Impfung nachweisbar blieben.' },
        { bold: 'Signifikant verzögertes Wiederauftreten', text: 'Patienten mit nachgewiesener T-Zell-Aktivierung zeigten im Beobachtungszeitraum ein signifikant längeres rezidivfreies Überleben als Patienten ohne Immunantwort.' }
      ]
    },
    Spanish: {
      topic: 'Inmunología y Oncología',
      title: 'Las vacunas personalizadas de ARNm estimulan células T duraderas contra el cáncer de páncreas',
      date: '10 de mayo de 2023',
      authors: 'L. A. Rojas, Z. Sethna, K. C. Soares et al. · Memorial Sloan Kettering',
      summaryHeading: '🎯 Resumen',
      summaryText: 'En un ensayo clínico de fase I, los investigadores evaluaron una vacuna de ARNm personalizada contra el cáncer de páncreas. Mediante la secuenciación del tumor de cada paciente, se sintetizaron vacunas a medida dirigidas hasta a 20 neoantígenos únicos, combinadas con inmunoterapia y cirugía para entrenar a los linfocitos T frente a restos tumorales microscópicos.',
      contextHeading: '🌏 Contexto general',
      contextText: 'El adenocarcinoma ductal de páncreas se considera históricamente un tumor inmunológicamente «frío» e insensible a la inmunoterapia convencional. Este estudio demuestra que las vacunas de ARNm personalizadas pueden inducir una inmunidad específica y duradera incluso en tumores con baja carga mutacional, abriendo un nuevo paradigma en la oncología de precisión adyuvante.',
      resultsHeading: '🔑 Resultados clave',
      results: [
        { bold: 'Alta tasa de respuesta e inmunidad duradera', text: '8 de 16 pacientes (50 %) desarrollaron respuestas potentes de células T específicas frente a neoantígenos que persistieron hasta dos años después de la vacunación.' },
        { bold: 'Supervivencia libre de recaída prolongada', text: 'Los pacientes con respuesta verificada de células T mostraron una supervivencia libre de recurrencia significativamente mayor en comparación con los no respondedores.' }
      ]
    },
    Portuguese: {
      topic: 'Imunologia e Oncologia',
      title: 'Vacinas personalizadas de mRNA estimulam células T de forma duradoura no cancro do pâncreas',
      date: '10 de maio de 2023',
      authors: 'L. A. Rojas, Z. Sethna, K. C. Soares et al. · Memorial Sloan Kettering',
      summaryHeading: '🎯 Resumo',
      summaryText: 'Num ensaio clínico de fase I, investigadores avaliaram uma vacina de mRNA personalizada contra o cancro do pâncreas. Através da sequenciação do tumor de cada doente, foram produzidas vacinas à medida direcionadas até 20 mutações únicas (neoantigénios), combinadas com imunoterapia e cirurgia para treinar as células T a eliminar doença residual microscópica.',
      contextHeading: '🌏 Contexto mais amplo',
      contextText: 'O adenocarcinoma pancreático é historicamente considerado imunologicamente «frio» e resistente à imunoterapia convencional. Este estudo demonstra que vacinas personalizadas de mRNA podem despertar uma resposta imunitária direcionada e duradoura mesmo em tumores com baixa carga mutacional, abrindo um novo caminho na oncologia de precisão adjuvante.',
      resultsHeading: '🔑 Principais resultados',
      results: [
        { bold: 'Imunogenicidade robusta e duradoura', text: '8 em 16 doentes (50%) desenvolveram respostas fortes de células T específicas para neoantigénios, que persistiram até dois anos após a vacinação.' },
        { bold: 'Sobrevivência livre de recidiva prolongada', text: 'Os doentes com atividade verificada de células T induzida pela vacina apresentaram uma sobrevivência livre de recidiva significativamente superior.' }
      ]
    },
    Hindi: {
      topic: 'इम्यूनोलॉजी और ऑन्कोलॉजी',
      title: 'व्यक्तिगत mRNA वैक्सीन अग्नाशय के कैंसर में दीर्घकालिक टी-सेल प्रतिरक्षा प्रतिक्रिया को सक्रिय करती है',
      date: '10 मई 2023',
      authors: 'L. A. Rojas, Z. Sethna, K. C. Soares et al. · Memorial Sloan Kettering',
      summaryHeading: '🎯 सारांश',
      summaryText: 'चरण I के क्लिनिकल परीक्षण में, शोधकर्ताओं ने अग्नाशय (पैंक्रियाटिक) कैंसर के लिए एक व्यक्तिगत mRNA वैक्सीन का मूल्यांकन किया। प्रत्येक रोगी के ट्यूमर की सीक्वेंसिंग करके 20 अनूठे म्यूटेशन (नियोएंटीजन) को लक्षित करने वाले टीके तैयार किए गए। सूक्ष्म ट्यूमर अवशेषों को नष्ट करने के लिए इसे इम्यूनोथेरेपी और सर्जरी के साथ दिया गया।',
      contextHeading: '🌏 व्यापक संदर्भ',
      contextText: 'अग्नाशय का कैंसर सबसे घातक कैंसर रूपों में से एक है और इसे पारंपरिक इम्यूनोथेरेपी के प्रति असंवेदनशील माना जाता रहा है। यह अध्ययन दर्शाता है कि व्यक्तिगत mRNA टीके कम म्यूटेशन वाले ट्यूमर में भी लक्षित प्रतिरक्षा प्रतिक्रिया जगा सकते हैं, जिससे सटीक ऑन्कोलॉजी में एक नया मार्ग खुलता है।',
      resultsHeading: '🔑 मुख्य परिणाम',
      results: [
        { bold: 'मजबूत और दीर्घकालिक प्रतिरक्षा प्रतिक्रिया', text: '16 में से 8 रोगियों (50%) में नियोएंटीजन-विशिष्ट टी-सेल प्रतिक्रियाएं विकसित हुईं, जो टीकाकरण के दो साल बाद तक बनी रहीं।' },
        { bold: 'कैंसर की पुनरावृत्ति में उल्लेखनीय देरी', text: 'जिन रोगियों में टी-सेल प्रतिक्रिया देखी गई, उनमें अनुवर्ती अवधि के दौरान कैंसर-मुक्त जीवित रहने की अवधि काफी अधिक रही।' }
      ]
    },
    Mandarin: {
      topic: '免疫学与肿瘤学',
      title: '个性化 mRNA 新抗原疫苗在胰腺癌患者中激发持久的 T 细胞免疫反应',
      date: '2023年5月10日',
      authors: 'L. A. Rojas, Z. Sethna, K. C. Soares et al. · Memorial Sloan Kettering',
      summaryHeading: '🎯 研究摘要',
      summaryText: '在一项 I 期临床试验中，研究人员评估了针对胰腺癌的定制化 mRNA 疫苗。通过对每位患者的肿瘤进行测序，研究团队合成了靶向最多 20 种独特突变（新抗原）的个体化疫苗，并与免疫治疗及标准手术联合使用，以训练宿主 T 细胞清除微小残留病灶。',
      contextHeading: '🌏 研究背景与意义',
      contextText: '胰腺导管腺癌致死率极高，传统上被视为免疫“冷”肿瘤，对常规免疫检查点抑制剂缺乏反应。本研究证实，个性化新抗原 mRNA 疫苗即使在低突变负荷肿瘤中也能诱导持久的临床免疫应答，为辅助精准肿瘤学开辟了全新范式。',
      resultsHeading: '🔑 关键结果',
      results: [
        { bold: '强效且持久的免疫原性', text: '16 名患者中有 8 名（50%）产生了针对肿瘤新抗原的特异性 T 细胞反应，并在接种后持续长达两年。' },
        { bold: '无复发生存期显著延长', text: '在随访期内，产生疫苗诱导 T 细胞应答的患者相比无应答患者表现出显著更长的无复发生存期。' }
      ]
    }
  }
};

const LoginView = ({ handleGoogleLogin, handleAppleLogin }) => {
  const { language, setLanguage, languages } = useLanguage();

  const activeContent =
    REAL_SAMPLE_ARTICLE.content[language] ||
    REAL_SAMPLE_ARTICLE.content.English ||
    REAL_SAMPLE_ARTICLE.content.Swedish;

  const scrollToAuth = () => {
    const authElement = document.getElementById('auth-card');
    if (authElement) {
      authElement.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-[#f8fafc] text-slate-900 font-sans flex flex-col justify-between">
      
      {/* Top Navbar */}
      <header className="w-full max-w-6xl mx-auto px-6 py-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-slate-900 rounded-xl flex items-center justify-center text-white shadow-md">
            <BookOpen size={20} />
          </div>
          <span className="font-serif italic font-bold text-xl text-slate-900 tracking-tight">
            Science Monitor
          </span>
        </div>

        <div className="flex items-center gap-3">
          <Link
            to="/about"
            className="text-xs font-semibold text-slate-600 hover:text-slate-900 bg-white border border-slate-200 rounded-full px-3.5 py-1.5 shadow-xs hover:border-slate-300 transition-colors cursor-pointer"
          >
            {t(language, 'about_title')}
          </Link>

          <div className="relative inline-flex items-center bg-white border border-slate-200 rounded-full px-2.5 py-1 shadow-xs hover:border-slate-300 transition-colors">
            <Globe size={14} className="text-slate-500 mr-1.5" />
            <select
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              className="bg-transparent text-xs font-semibold text-slate-700 focus:outline-hidden cursor-pointer"
            >
              {languages.map((lang) => (
                <option key={lang} value={lang}>{lang}</option>
              ))}
            </select>
          </div>

          <div className="hidden sm:flex items-center gap-2 text-xs font-semibold text-slate-600 bg-white px-3.5 py-1.5 rounded-full border border-slate-200 shadow-xs">
            <ShieldCheck size={14} className="text-emerald-600" />
            <span>{t(language, 'login_badge_verified')}</span>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="w-full max-w-6xl mx-auto px-6 pt-6 pb-12 md:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-14 items-center">
          
          {/* VÄNSTER: Rubrik & primär CTA */}
          <div className="lg:col-span-5 text-left space-y-6">
            <div className="inline-flex items-center gap-2 px-3.5 py-1 rounded-full bg-blue-50 border border-blue-100 text-blue-700 text-xs font-semibold tracking-wide">
              <Sparkles size={14} className="text-blue-500" />
              <span>{t(language, 'login_hero_badge')}</span>
            </div>

            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-serif text-slate-900 tracking-tight leading-[1.15]">
              {t(language, 'login_hero_title1')} <br />
              <span className="italic text-slate-700">{t(language, 'login_hero_title2')}</span>
            </h1>

            <p className="text-slate-600 text-base sm:text-lg leading-relaxed">
              {t(language, 'login_hero_desc')}
            </p>

            <div className="pt-2 space-y-3">
              <button
                onClick={scrollToAuth}
                className="inline-flex items-center justify-center gap-2.5 w-full sm:w-auto px-6 py-3.5 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-medium text-sm sm:text-base shadow-sm transition-all hover:translate-y-[-1px] active:scale-[0.99] cursor-pointer"
              >
                <span>{t(language, 'login_hero_cta')}</span>
                <ArrowRight size={18} />
              </button>
              
              <div className="flex items-center gap-4 text-xs text-slate-500 font-sans">
                <span className="flex items-center gap-1.5">
                  <CheckCircle2 size={14} className="text-emerald-500" /> {t(language, 'login_trust_free_trial')}
                </span>
                <span className="flex items-center gap-1.5">
                  <CheckCircle2 size={14} className="text-emerald-500" /> {t(language, 'login_trust_no_card')}
                </span>
              </div>
            </div>
          </div>

          {/* HÖGER: Riktigt produktkort med exakta sektioner från Inbox */}
          <div className="lg:col-span-7">
            <div className="relative mx-auto w-full max-w-xl">
              <div className="absolute -inset-1.5 bg-gradient-to-r from-blue-100 to-indigo-100 rounded-3xl blur-xl opacity-60 -z-10" />

              <div className="bg-white rounded-3xl border border-slate-200/90 shadow-md shadow-slate-100 overflow-hidden text-left flex flex-col">
                
                {/* Header-sektion */}
                <div className="p-6 sm:p-8 border-b border-slate-100 shrink-0">
                  <div className="flex justify-between items-center mb-4">
                    <span className="text-[10px] font-bold text-blue-700 uppercase tracking-widest bg-blue-50 border border-blue-100/60 px-3 py-1 rounded-full">
                      {activeContent.topic}
                    </span>
                  </div>

                  <h3 className="text-xl sm:text-2xl font-serif mb-4 leading-snug text-slate-900">
                    {activeContent.title}
                  </h3>

                  <div className="space-y-1.5 pt-1">
                    <p className="text-xs sm:text-sm text-slate-600 font-medium italic flex items-start gap-2">
                      <User size={15} className="shrink-0 mt-0.5 text-slate-400" />
                      <span className="leading-relaxed">{activeContent.authors || REAL_SAMPLE_ARTICLE.authors}</span>
                    </p>
                    <p className="text-[11px] sm:text-xs text-slate-400 uppercase tracking-wider font-sans">
                      {REAL_SAMPLE_ARTICLE.venue} • {activeContent.date}
                    </p>
                  </div>
                </div>

                {/* Sammanfattning med exakta rubriker och format från Inbox */}
                <div className="p-6 sm:p-8 bg-slate-50/50 flex-1 flex flex-col justify-between space-y-6">
                  <div>
                    {/* AI SUMMARY badge som i inkorgen */}
                    <div className="flex items-center gap-1.5 text-[11px] font-bold text-indigo-600 uppercase tracking-widest mb-6">
                      <Sparkles size={14} /> 
                      <span>{t(language, 'login_card_ai_summary')}</span>
                    </div>

                    <div className="space-y-6 text-slate-700 text-sm sm:text-base font-serif">
                      
                      {/* Sektion 1: Sammanfattning */}
                      <div className="space-y-2">
                        <h4 className="font-sans font-bold text-base text-slate-900 flex items-center gap-2">
                          {`🎯 ${t(language, 'login_summary_heading_summary')}`}
                        </h4>
                        <p className="leading-relaxed text-slate-700">
                          {activeContent.summaryText}
                        </p>
                      </div>

                      {/* Sektion 2: Större Sammanhang */}
                      <div className="space-y-2">
                        <h4 className="font-sans font-bold text-base text-slate-900 flex items-center gap-2">
                          {`🌏 ${t(language, 'login_summary_heading_context')}`}
                        </h4>
                        <p className="leading-relaxed text-slate-700">
                          {activeContent.contextText}
                        </p>
                      </div>

                      {/* Sektion 3: Nyckelresultat med punktlista */}
                      <div className="space-y-3">
                        <h4 className="font-sans font-bold text-base text-slate-900 flex items-center gap-2">
                          {`🔑 ${t(language, 'login_summary_heading_results')}`}
                        </h4>
                        <ul className="space-y-2.5">
                          {activeContent.results.map((res, idx) => (
                            <li key={idx} className="space-y-0.5">
                              <p className="leading-relaxed text-slate-800">
                                <span className="font-sans font-bold text-slate-900">
                                  • {res.bold}:
                                </span>{' '}
                                {res.text}
                              </p>
                            </li>
                          ))}
                        </ul>
                      </div>

                    </div>
                  </div>

                  {/* Fot med originalstudie som leder direkt till Nature */}
                  <div className="pt-5 border-t border-slate-200/70 flex justify-between items-center shrink-0">
                    <a
                      href={REAL_SAMPLE_ARTICLE.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs font-bold text-blue-600 hover:text-blue-800 flex items-center gap-1.5 cursor-pointer transition-colors"
                    >
                      <span>{t(language, 'login_card_read_original')}</span>
                      <ExternalLink size={13} />
                    </a>
                    <span className="text-[11px] text-slate-400 font-sans tracking-wide">
                      {t(language, 'login_card_peer_reviewed')}
                    </span>
                  </div>
                </div>

              </div>
            </div>
          </div>

        </div>
      </section>

      {/* Auth-sektion under fold */}
      <section id="auth-card" className="w-full max-w-md mx-auto px-6 py-12 md:py-16">
        <div className="bg-white rounded-3xl shadow-xl shadow-slate-200/60 p-8 sm:p-10 border border-slate-200">
          
          <div className="text-center mb-6">
            <h2 className="text-2xl font-serif font-bold text-slate-900">
              {t(language, 'login_card_title')}
            </h2>
            <p className="text-slate-500 text-xs sm:text-sm mt-1">
              {t(language, 'login_card_subtitle')}
            </p>
          </div>

          <div className="space-y-3.5">
            <button 
              onClick={handleGoogleLogin} 
              className="w-full flex items-center justify-center gap-3 bg-white border border-slate-300 hover:border-slate-400 py-3.5 px-4 rounded-2xl font-bold text-slate-700 hover:bg-slate-50 transition-all shadow-xs active:scale-95 cursor-pointer text-sm"
            >
              <img src="https://www.google.com/favicon.ico" alt="Google" className="w-4 h-4" />
              <span>{t(language, 'login_google')}</span>
            </button>

            <button 
              onClick={handleAppleLogin} 
              className="w-full flex items-center justify-center gap-3 bg-black hover:bg-slate-900 py-3.5 px-4 rounded-2xl font-bold text-white transition-all shadow-xs active:scale-95 cursor-pointer text-sm"
            >
              <svg className="w-4 h-4" viewBox="0 0 384 512" fill="currentColor">
                <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"/>
              </svg>
              <span>{t(language, 'login_apple')}</span>
            </button>
          </div>

          <div className="mt-6 pt-6 border-t border-slate-100 space-y-2.5">
            <div className="flex items-center gap-2 text-xs text-slate-600 font-medium">
              <CheckCircle2 size={15} className="text-emerald-500 shrink-0" />
              <span>{t(language, 'login_trust_14_days')}</span>
            </div>
            <div className="flex items-center gap-2 text-xs text-slate-600 font-medium">
              <CheckCircle2 size={15} className="text-emerald-500 shrink-0" />
              <span>{t(language, 'login_trust_no_card')}</span>
            </div>
          </div>

          <div className="pt-6 mt-6 border-t border-slate-100 text-center">
            <p className="text-[11px] text-slate-400 leading-relaxed">
              {t(language, 'login_terms_prefix')}
              <Link 
                to="/terms" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-slate-600 hover:text-indigo-600 underline transition-colors"
              >
                {t(language, 'login_terms_link')}
              </Link>
              {t(language, 'login_terms_and')}
              <Link 
                to="/privacy" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-slate-600 hover:text-indigo-600 underline transition-colors"
              >
                {t(language, 'login_privacy_link')}
              </Link>.
            </p>
          </div>

        </div>
      </section>

      {/* Footer */}
      <footer className="w-full max-w-6xl mx-auto px-6 py-6 border-t border-slate-200/60 flex flex-col sm:flex-row items-center justify-between text-xs text-slate-400 gap-4">
        <p>© {new Date().getFullYear()} {t(language, 'login_footer_copy')}</p>
        <div className="flex items-center gap-6">
          <Link to="/about" className="hover:text-slate-600 transition-colors font-medium">
            {t(language, 'about_title')}
          </Link>
          <Link to="/terms" className="hover:text-slate-600 transition-colors">
            {t(language, 'login_footer_terms')}
          </Link>
          <Link to="/privacy" className="hover:text-slate-600 transition-colors">
            {t(language, 'login_footer_privacy')}
          </Link>
        </div>
      </footer>

    </div>
  );
};

export default LoginView;
